import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierVehicle,
  type UpdateVehicleVars,
  useUpdateVehicle,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface EditVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: CarrierVehicle;
}

const TYPE_OPTIONS = [
  "BICYCLE",
  "MOTORCYCLE",
  "CAR",
  "VAN",
  "TRUCK",
  "REFRIGERATED_VAN",
  "REFRIGERATED_TRUCK",
] as const;

const STATUS_OPTIONS = [
  "AVAILABLE",
  "IN_SERVICE",
  "MAINTENANCE",
  "OUT_OF_SERVICE",
] as const;

/**
 * Edit dialog for a single vehicle. Server allowlist excludes code / vin /
 * licensePlate (unique IDs) and depotId (scope), so the UI only exposes
 * type, status, make, model, year.
 */
export function EditVehicleDialog({
  open,
  onOpenChange,
  vehicle,
}: EditVehicleDialogProps) {
  const update = useUpdateVehicle();
  const submissionInFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [type, setType] = useState<string>(vehicle.type);
  const [status, setStatus] = useState<string>(vehicle.status);
  const [make, setMake] = useState<string>(vehicle.make ?? "");
  const [model, setModel] = useState<string>(vehicle.model ?? "");
  const [year, setYear] = useState<string>(
    vehicle.year !== null && vehicle.year !== undefined
      ? String(vehicle.year)
      : ""
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionInFlight.current) return;
    if (vehicle.year !== null && vehicle.year !== undefined && year === "") {
      setValidationError("Year cannot be cleared for an existing vehicle.");
      return;
    }
    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setValidationError(null);
    const body: UpdateVehicleVars["body"] = {};
    if (type !== vehicle.type) body.type = type;
    if (status !== vehicle.status) body.status = status;
    if (make !== (vehicle.make ?? "")) body.make = make;
    if (model !== (vehicle.model ?? "")) body.model = model;
    const yearNum = year === "" ? undefined : Number.parseInt(year, 10);
    if (yearNum !== undefined && yearNum !== vehicle.year) body.year = yearNum;

    try {
      await update.mutateAsync({ vehicleId: vehicle.id, body });
      toast.success("Vehicle updated.");
      onOpenChange(false);
    } catch (error) {
      setSubmitError(
        hasHttpStatus(error, 403)
          ? "You do not have permission to update this vehicle."
          : "Failed to update vehicle. Try again."
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  const pending = update.isPending || submitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
      title={`Edit vehicle — ${vehicle.code}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="vh-type" className="block text-sm font-medium mb-1">
            Type
          </label>
          <NativeSelect
            id="vh-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={pending}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, " ")}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div>
          <label htmlFor="vh-status" className="block text-sm font-medium mb-1">
            Status
          </label>
          <NativeSelect
            id="vh-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={pending}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="vh-make" className="block text-sm font-medium mb-1">
              Make
            </label>
            <Input
              id="vh-make"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label
              htmlFor="vh-model"
              className="block text-sm font-medium mb-1"
            >
              Model
            </label>
            <Input
              id="vh-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        <div>
          <label htmlFor="vh-year" className="block text-sm font-medium mb-1">
            Year
          </label>
          <Input
            id="vh-year"
            type="number"
            min={1950}
            max={2100}
            value={year}
            onChange={(e) => setYear(e.target.value)}
            disabled={pending}
            aria-invalid={validationError ? true : undefined}
          />
        </div>

        {validationError && (
          <p role="alert" className="text-sm text-destructive">
            {validationError}
          </p>
        )}

        {(submitError || update.isError) && (
          <p
            role="alert"
            aria-label="Vehicle update failed"
            className="text-sm text-destructive"
          >
            {submitError ?? "Failed to update vehicle. Try again."}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="touch"
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" size="touch" disabled={pending}>
            {pending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
